import { FC } from 'react';
import CustomTable from '../../../shared/CustomTable/CustomTable';
import { rigaDensita, rigaMallo } from '../allenamenti.logica';
import { ITrainingBlock } from '../../../models/allenamenti';
import { FALLBACK_CATEGORY_COLOR } from '../allenamenti.constants';
import { formatLocalDateIt, formatMinutes, responsibleLabel } from './analysis.constants';
import Typhography from '../../../shared/Typhography/Typhography';
import styles from './BlocksTable.module.scss';

interface IBlocksTableProps {
  blocks: ITrainingBlock[];
  isLoading: boolean;
  bySlug: Map<string, { labelIt: string; color: string }>;
}

const HEADERS = [
  'Data',
  'Categoria',
  'Titolo',
  'Durata',
  'Campo · densità',
  'Mallo · flusso',
  'Responsabile',
] as const;

// Le righe compatte densità/Mallo arrivano da allenamenti.logica (stessa
// fonte di BlockCard): qui si sostituisce solo il vuoto con «—» da tabella.

/** Tabella dei blocchi grezzi del periodo — CustomTable riusata as-is (design M2/M3 §1.3). */
const BlocksTable: FC<IBlocksTableProps> = ({ blocks, isLoading, bySlug }) => {
  const rows = [...blocks]
    .sort((a, b) => a.localDate.localeCompare(b.localDate))
    .map((block) => {
      const categorie = (block.categoryIds ?? []).map((id) => bySlug.get(id));
      const sotto = (block.subcategoryIds ?? []).map((id) => bySlug.get(id));
      return {
        _id: block._id,
        date: formatLocalDateIt(block.localDate),
        // Più categorie su una riga di tabella: unite da un punto medio. Il
        // colore è quello della prima — la pastiglia è una sola.
        categoryLabel: categorie
          .map((c, i) => c?.labelIt ?? block.categoryIds?.[i] ?? '')
          .filter(Boolean)
          .join(' · '),
        categoryColor: categorie[0]?.color ?? FALLBACK_CATEGORY_COLOR,
        subcategoryLabel: sotto
          .map((c, i) => c?.labelIt ?? block.subcategoryIds?.[i] ?? '')
          .filter(Boolean)
          .join(' · '),
        title: block.title,
        minutesLabel: formatMinutes(block.durationMinutes),
        density: rigaDensita(block, true) ?? '—',
        mallo: rigaMallo(block, true) ?? '—',
        responsible: responsibleLabel(block.responsible),
      };
    });

  return (
    <div className={styles.wrap}>
      <Typhography type="h3" size="bold-14" className={styles.title}>Blocchi del periodo</Typhography>
      <CustomTable
        isLoading={isLoading}
        headers={[...HEADERS]}
        data={rows}
        fields={{
          Data: { key: 'date' },
          Categoria: {
            key: 'categoryLabel',
            customComponent: (_value, record) => (
              <span className={styles.categoryCell}>
                <span className={styles.dot} style={{ background: record.categoryColor }} />
                {record.categoryLabel}
                {record.subcategoryLabel && <span className={styles.sub}> · {record.subcategoryLabel}</span>}
              </span>
            ),
          },
          Titolo: { key: 'title' },
          Durata: { key: 'minutesLabel', align: 'end' },
          'Campo · densità': { key: 'density' },
          'Mallo · flusso': { key: 'mallo' },
          Responsabile: { key: 'responsible' },
        }}
      />
    </div>
  );
};

export default BlocksTable;
